import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, GeoJSON, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { formatDateWithOptionalTime } from '../../utils/dateUtils';

// Fix for Leaflet default icon issues
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
});

// Custom marker clustering component using react-leaflet hooks
function EventClusterLayer({ events, handleEdit, showModal, departments }) {
  const map = useMap();
  
  useEffect(() => {
    if (!events || events.length === 0 || !departments) return;
    
    // Create a function to generate a cluster icon with count
    function createClusterIcon(cluster) {
      const count = cluster.getChildCount();
      let size = 'small';
      
      if (count > 10) {
        size = 'medium';
      }
      if (count > 20) {
        size = 'large';
      }
      
      return L.divIcon({
        html: `<div class="cluster-icon cluster-${size}">${count}</div>`,
        className: 'custom-marker-cluster',
        iconSize: L.point(40, 40)
      });
    }
    
    // Add CSS for the cluster icons to the document head
    const style = document.createElement('style');
    style.textContent = `
      .custom-marker-cluster {
        background: none;
      }
      .cluster-icon {
        display: flex;
        justify-content: center;
        align-items: center;
        color: white;
        font-weight: bold;
        border-radius: 50%;
        background-color: #1976d2;
        box-shadow: 0 0 0 5px rgba(25, 118, 210, 0.3);
      }
      .cluster-small {
        width: 30px;
        height: 30px;
        font-size: 12px;
      }
      .cluster-medium {
        width: 35px;
        height: 35px;
        font-size: 13px;
      }
      .cluster-large {
        width: 40px;
        height: 40px;
        font-size: 14px;
      }
      
      /* Style for location clusters at max zoom */
      .location-cluster {
        background-color: #ff4081;
        width: 25px;
        height: 25px;
        display: flex;
        justify-content: center;
        align-items: center;
        color: white;
        font-weight: bold;
        border-radius: 50%;
        box-shadow: 0 0 0 3px rgba(255, 64, 129, 0.3);
      }
    `;
    document.head.appendChild(style);
    
    // Import leaflet.markercluster dynamically
    const script = document.createElement('script');
    script.src = 'https://unpkg.com/leaflet.markercluster@1.5.3/dist/leaflet.markercluster.js';
    script.async = true;
    
    script.onload = () => {
      // Group events by department
      const eventsByDepartment = {};
      
      // Function to find which department contains a point
      function findDepartmentForPoint(lat, lng) {
        let foundDepartment = null;
        
        departments.features.forEach(dept => {
          // Create a GeoJSON layer for the department to use its isMarkerInsidePolygon method
          const layer = L.geoJSON(dept);
          
          // Check if the point is inside this department
          if (layer.getBounds().contains([lat, lng])) {
            // Get the department code
            foundDepartment = {
              code: dept.properties.code || 'unknown',
              name: dept.properties.nom || 'Unknown Department'
            };
          }
        });
        
        return foundDepartment;
      }
      
      // Process each event to find its department
      events.forEach(event => {
        if (event.location && event.location.geo && typeof event.location.geo === 'string') {
          const coordinates = event.location.geo.split(',').map(coord => parseFloat(coord.trim()));
          
          if (coordinates.length === 2 && !isNaN(coordinates[0]) && !isNaN(coordinates[1])) {
            // First try to use postal code if available (more reliable)
            let deptCode = 'unknown';
            let deptName = 'Unknown Department';
            
            if (event.location.postalCode && event.location.postalCode.length >= 2) {
              deptCode = event.location.postalCode.substring(0, 2);
              
              // Try to find department name from the code
              const deptFeature = departments.features.find(
                dept => dept.properties.code === deptCode
              );
              if (deptFeature) {
                deptName = deptFeature.properties.nom;
              }
            } else {
              // If no postal code, try to find the department by coordinates
              const deptInfo = findDepartmentForPoint(coordinates[0], coordinates[1]);
              if (deptInfo) {
                deptCode = deptInfo.code;
                deptName = deptInfo.name;
              }
            }
            
            // Initialize array for this department if it doesn't exist
            if (!eventsByDepartment[deptCode]) {
              eventsByDepartment[deptCode] = {
                events: [],
                name: deptName,
                code: deptCode
              };
            }
            
            // Add event to the appropriate department
            eventsByDepartment[deptCode].events.push({
              ...event,
              coordinates: coordinates
            });
          }
        }
      });
      
      // Further group events by exact location
      Object.keys(eventsByDepartment).forEach(deptCode => {
        const deptData = eventsByDepartment[deptCode];
        const locationGroups = {};
        
        // Group events by exact coordinates
        deptData.events.forEach(event => {
          const locationKey = event.coordinates.join(',');
          
          if (!locationGroups[locationKey]) {
            locationGroups[locationKey] = {
              coordinates: event.coordinates,
              events: []
            };
          }
          
          locationGroups[locationKey].events.push(event);
        });
        
        deptData.locationGroups = locationGroups;
      });
      
      // Create cluster groups for each department
      const departmentLayers = {};
      
      Object.keys(eventsByDepartment).forEach(deptCode => {
        const deptData = eventsByDepartment[deptCode];
        const locationGroups = deptData.locationGroups;
        
        // Skip if no events in this department
        if (deptData.events.length === 0) return;
        
        // Create a marker cluster group for this department with enhanced options
        const clusterGroup = L.markerClusterGroup({
          showCoverageOnHover: false,
          maxClusterRadius: 40, // Smaller radius to avoid over-clustering
          spiderfyOnMaxZoom: false, // Don't auto-spiderfy on max zoom
          zoomToBoundsOnClick: true,
          disableClusteringAtZoom: null, // Never disable clustering, even at max zoom
          iconCreateFunction: createClusterIcon
        });
        
        // Add markers or mini-clusters for each location group
        Object.keys(locationGroups).forEach(locationKey => {
          const locationData = locationGroups[locationKey];
          const eventsAtLocation = locationData.events;
          
          if (eventsAtLocation.length === 1) {
            // If only one event at this location, create a normal marker
            const event = eventsAtLocation[0];
            const marker = L.marker(event.coordinates);
            
            // Create popup content
            const popupContent = createPopupContent(event, handleEdit, showModal);
            
            // Bind popup to marker
            marker.bindPopup(popupContent);
            
            // Add the marker to the cluster group
            clusterGroup.addLayer(marker);
          } else {
            // If multiple events at this location, create a special mini-cluster
            const locationCluster = L.marker(locationData.coordinates, {
              icon: L.divIcon({
                html: `<div class="location-cluster">${eventsAtLocation.length}</div>`,
                className: 'custom-location-cluster',
                iconSize: L.point(25, 25)
              })
            });
            
            // Create popup with list of events and slider
            const popupContent = document.createElement('div');
            popupContent.style.minWidth = "250px";
            
            // Location title
            const title = document.createElement('h5');
            title.className = "mb-2 fw-bold";
            title.textContent = `${eventsAtLocation.length} événements à cet endroit`;
            popupContent.appendChild(title);
            
            // Number of events to show per page
            const eventsPerPage = 3;
            const totalPages = Math.ceil(eventsAtLocation.length / eventsPerPage);
            let currentPage = 0;
            
            // Container for the slider
            const sliderContainer = document.createElement('div');
            sliderContainer.className = "position-relative";
            
            // Create slides wrapper that will hold all pages
            const slidesWrapper = document.createElement('div');
            slidesWrapper.className = "overflow-hidden";
            sliderContainer.appendChild(slidesWrapper);
            
            // Create the slider track
            const sliderTrack = document.createElement('div');
            sliderTrack.className = "d-flex";
            sliderTrack.style.transition = "transform 0.3s ease";
            sliderTrack.style.width = `${totalPages * 100}%`;
            slidesWrapper.appendChild(sliderTrack);
            
            // Create pages
            for (let page = 0; page < totalPages; page++) {
              const pageElement = document.createElement('div');
              pageElement.className = "flex-shrink-0";
              pageElement.style.width = `${100 / totalPages}%`;
              
              // Create events list for this page
              const pageList = document.createElement('div');
              pageList.className = "list-group";
              
              // Add events for this page (up to 3)
              const startIdx = page * eventsPerPage;
              const endIdx = Math.min(startIdx + eventsPerPage, eventsAtLocation.length);
              
              for (let i = startIdx; i < endIdx; i++) {
                const event = eventsAtLocation[i];
                
                const eventItem = document.createElement('div');
                eventItem.className = "list-group-item list-group-item-action px-2 py-2";
                eventItem.style.cursor = "pointer";
                
                // Event title with date
                const eventTitle = document.createElement('div');
                eventTitle.className = "fw-bold";
                eventTitle.textContent = event.title;
                eventItem.appendChild(eventTitle);
                
                // Date
                const eventDate = document.createElement('div');
                eventDate.className = "small";
                eventDate.textContent = formatDateWithOptionalTime(event.date);
                eventItem.appendChild(eventDate);
                
                // Organizer if available
                if (event.organizer) {
                  const eventOrg = document.createElement('div');
                  eventOrg.className = "small text-primary";
                  eventOrg.textContent = event.organizer;
                  eventItem.appendChild(eventOrg);
                }
                
                // Add action buttons as small icons
                const actionBtns = document.createElement('div');
                actionBtns.className = "mt-1";
                
                const editBtn = document.createElement('button');
                editBtn.className = "btn btn-sm btn-outline-primary me-1 py-0 px-1";
                editBtn.innerHTML = '<i class="bi bi-pencil"></i>';
                editBtn.onclick = (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleEdit(event.id);
                };
                actionBtns.appendChild(editBtn);
                
                const deleteBtn = document.createElement('button');
                deleteBtn.className = "btn btn-sm btn-outline-danger py-0 px-1";
                deleteBtn.innerHTML = '<i class="bi bi-trash"></i>';
                deleteBtn.onclick = (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  showModal(event.id);
                };
                actionBtns.appendChild(deleteBtn);
                
                eventItem.appendChild(actionBtns);
                
                // Show details on click
                eventItem.addEventListener('click', () => {
                  L.popup()
                    .setLatLng(event.coordinates)
                    .setContent(createPopupContent(event, handleEdit, showModal))
                    .openOn(map);
                });
                
                pageList.appendChild(eventItem);
              }
              
              pageElement.appendChild(pageList);
              sliderTrack.appendChild(pageElement);
            }
            
            // Add pagination if needed
            if (totalPages > 1) {
              // Add navigation controls
              const navControls = document.createElement('div');
              navControls.className = "d-flex justify-content-between align-items-center my-2";
              
              // Pagination indicator
              const paginationInfo = document.createElement('div');
              paginationInfo.className = "text-center small";
              paginationInfo.textContent = `Page 1/${totalPages}`;
              paginationInfo.id = "pagination-info";
              
              // Previous button
              const prevBtn = document.createElement('button');
              prevBtn.className = "btn btn-sm btn-outline-secondary";
              prevBtn.innerHTML = '<i class="bi bi-chevron-left"></i>';
              prevBtn.disabled = true;
              prevBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                if (currentPage > 0) {
                  currentPage--;
                  updateSlider();
                }
              });
              
              // Next button
              const nextBtn = document.createElement('button');
              nextBtn.className = "btn btn-sm btn-outline-secondary";
              nextBtn.innerHTML = '<i class="bi bi-chevron-right"></i>';
              nextBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                if (currentPage < totalPages - 1) {
                  currentPage++;
                  updateSlider();
                }
              });
              
              navControls.appendChild(prevBtn);
              navControls.appendChild(paginationInfo);
              navControls.appendChild(nextBtn);
              
              // Function to update the slider position
              function updateSlider() {
                sliderTrack.style.transform = `translateX(-${currentPage * (100 / totalPages)}%)`;
                paginationInfo.textContent = `Page ${currentPage + 1}/${totalPages}`;
                
                // Update button states
                prevBtn.disabled = currentPage === 0;
                nextBtn.disabled = currentPage === totalPages - 1;
              }
              
              popupContent.appendChild(navControls);
            }
            
            popupContent.appendChild(sliderContainer);
            
            // Bind popup to the cluster marker
            locationCluster.bindPopup(popupContent);
            
            // Add to the cluster group
            clusterGroup.addLayer(locationCluster);
          }
        });
        
        // Store the cluster group
        departmentLayers[deptCode] = clusterGroup;
        
        // Add to map
        map.addLayer(clusterGroup);
      });
      
      // Clean up function
      return () => {
        Object.values(departmentLayers).forEach(layer => {
          map.removeLayer(layer);
        });
        
        document.head.removeChild(style);
      };
    };
    
    document.head.appendChild(script);
    
    // CSS for leaflet.markercluster
    const cssLink = document.createElement('link');
    cssLink.rel = 'stylesheet';
    cssLink.href = 'https://unpkg.com/leaflet.markercluster@1.5.3/dist/MarkerCluster.css';
    document.head.appendChild(cssLink);
    
    const cssLink2 = document.createElement('link');
    cssLink2.rel = 'stylesheet';
    cssLink2.href = 'https://unpkg.com/leaflet.markercluster@1.5.3/dist/MarkerCluster.Default.css';
    document.head.appendChild(cssLink2);
    
    // Clean up on unmount
    return () => {
      document.head.removeChild(script);
      document.head.removeChild(cssLink);
      document.head.removeChild(cssLink2);
      
      const spiderScript = document.querySelector('script[src*="OverlappingMarkerSpiderfier"]');
      if (spiderScript) {
        document.head.removeChild(spiderScript);
      }
    };
  }, [events, map, handleEdit, showModal, departments]);
  
  return null;
}

// Helper function to create popup content for a single event
function createPopupContent(event, handleEdit, showModal) {
  const popupContent = document.createElement('div');
  popupContent.style.minWidth = "200px";
  
  // Event title
  const title = document.createElement('h5');
  title.className = "mb-0 fw-bold";
  title.textContent = event.title;
  popupContent.appendChild(title);
  
  // Organizer
  if (event.organizer) {
    const organizer = document.createElement('div');
    organizer.className = "text-primary mb-2";
    organizer.textContent = `${event.organizer}`;
    popupContent.appendChild(organizer);
  }
  
  // Date
  const dateDiv = document.createElement('div');
  dateDiv.className = "mb-1";
  const dateLabel = document.createElement('span');
  dateLabel.className = "fw-bold";
  dateLabel.textContent = "Date: ";
  dateDiv.appendChild(dateLabel);
  dateDiv.appendChild(document.createTextNode(formatDateWithOptionalTime(event.date)));
  if (event.endDate) {
    dateDiv.appendChild(document.createTextNode(` - ${formatDateWithOptionalTime(event.endDate)}`));
  }
  popupContent.appendChild(dateDiv);
  
  // Location
  const locationDiv = document.createElement('div');
  locationDiv.className = "mb-1";
  const locationLabel = document.createElement('span');
  locationLabel.className = "fw-bold";
  locationLabel.textContent = "Location: ";
  locationDiv.appendChild(locationLabel);
  locationDiv.appendChild(document.createTextNode(
    event.location.postalCode ? 
      `${event.location.cityName} (${event.location.postalCode})` : 
      event.location.cityName
  ));
  popupContent.appendChild(locationDiv);
  
  // Category
  if (event.category) {
    const categoryDiv = document.createElement('div');
    categoryDiv.className = "mb-1";
    const categoryLabel = document.createElement('span');
    categoryLabel.className = "fw-bold";
    categoryLabel.textContent = "Category: ";
    categoryDiv.appendChild(categoryLabel);
    categoryDiv.appendChild(document.createTextNode(event.category));
    popupContent.appendChild(categoryDiv);
  }
  
  // Speaker
  if (event.speaker) {
    const speakerDiv = document.createElement('div');
    speakerDiv.className = "mb-1";
    const speakerLabel = document.createElement('span');
    speakerLabel.className = "fw-bold";
    speakerLabel.textContent = "Speaker: ";
    speakerDiv.appendChild(speakerLabel);
    speakerDiv.appendChild(document.createTextNode(event.speaker));
    popupContent.appendChild(speakerDiv);
  }
  
  // Buttons
  const buttonsDiv = document.createElement('div');
  buttonsDiv.className = "d-flex mt-3";
  
  // Edit button
  const editBtn = document.createElement('button');
  editBtn.className = "btn btn-sm btn-primary me-2";
  editBtn.innerHTML = '<i class="bi bi-pencil"></i>';
  editBtn.onclick = (e) => {
    e.preventDefault();
    handleEdit(event.id);
  };
  buttonsDiv.appendChild(editBtn);
  
  // Delete button
  const deleteBtn = document.createElement('button');
  deleteBtn.className = "btn btn-sm btn-danger";
  deleteBtn.innerHTML = '<i class="bi bi-trash"></i>';
  deleteBtn.onclick = (e) => {
    e.preventDefault();
    showModal(event.id);
  };
  buttonsDiv.appendChild(deleteBtn);
  
  popupContent.appendChild(buttonsDiv);
  
  return popupContent;
}

const EventsMap = ({ events, handleEdit, showModal }) => {
  // Center of France coordinates
  const franceCenterPosition = [46.603354, 1.888334]; 
  const defaultZoom = 6;
  const [departments, setDepartments] = useState(null);

  // Fetch GeoJSON data for French departments
  useEffect(() => {
    fetch('https://raw.githubusercontent.com/gregoiredavid/france-geojson/master/departements.geojson')
      .then(response => response.json())
      .then(data => {
        setDepartments(data);
      })
      .catch(error => console.error('Error loading GeoJSON data:', error));
  }, []);

  // Style function for departments
  const departmentStyle = {
    weight: 1,
    opacity: 0.5,
    color: '#6c757d',
    fillOpacity: 0.1,
    fillColor: '#6c757d'
  };

  // Function to highlight department on hover
  const highlightFeature = (e) => {
    const layer = e.target;
    
    layer.setStyle({
      weight: 2,
      color: '#666',
      dashArray: '',
      fillOpacity: 0.3
    });
    
    if (!L.Browser.ie && !L.Browser.opera && !L.Browser.edge) {
      layer.bringToFront();
    }
  };
  
  // Function to reset highlight on mouseout
  const resetHighlight = (e) => {
    if (departments) {
      e.target.setStyle(departmentStyle);
    }
  };
  
  // Function to add listeners to department layers
  const onEachFeature = (feature, layer) => {
    layer.on({
      mouseover: highlightFeature,
      mouseout: resetHighlight,
    });
    if (feature.properties && feature.properties.nom) {
      layer.bindTooltip(feature.properties.nom);
    }
  };

  return (
    <div style={{ height: '800px', width: '100%', marginBottom: '20px' }}>
      <MapContainer 
        center={franceCenterPosition} 
        zoom={defaultZoom} 
        style={{ height: '100%', width: '100%' }}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        />
        
        {/* Add GeoJSON layer for departments */}
        {departments && (
          <GeoJSON 
            data={departments} 
            style={departmentStyle}
            onEachFeature={onEachFeature}
          />
        )}
        
        {/* Custom event clustering by department */}
        {departments && (
          <EventClusterLayer 
            events={events} 
            handleEdit={handleEdit} 
            showModal={showModal}
            departments={departments}
          />
        )}
      </MapContainer>
    </div>
  );
};

export default EventsMap;
